import { isNil } from 'lodash';
import { AvailabilityIndicator, CropAndBrandAvailability7, HybridAvailability7, MaterialAvailability7, SeriesAvailability7, TreatmentAvailability7 } from '../../Models/Responses/SeedResponseV7';
import UserFriendlyException from '../../Models/Exceptions/UserFriendlyException';

export type BrandLookup = Record<
	string, Omit<CropAndBrandAvailability7, 'Series'>>;

export type SeriesLookup = Record<
	string, 
	Omit<SeriesAvailability7, 'Hybrids'> & { 
		IsAvailable: boolean;
		HasAvailableTreatment: boolean;
		CropId: string;
		BrandId: string; 
	}>;

export type HybridLookup =Record<
	string, 
	Omit<HybridAvailability7, 'Treatments'> & { 
		IsAvailable: boolean;
		CropId: string;
		BrandId: string; 
		SeriesId: string; 
	}>;

export type TreatmentLookup = Record<
	string,
	Omit<TreatmentAvailability7, 'MaterialAvailability'> & { 
		IsAvailable: boolean;
		CropId: string;
		BrandId: string; 
		SeriesId: string; 
		HybridId: string; 
		/** Id from API. Unique to the hybrid. */
		TreatmentId: string;
		/** A globally unique id generated on the frontend. */
		TreatmentKey: string;
	}>;

export type MaterialLookup = Record<
	string,
	MaterialAvailability7 & { 
		IsAvailable: boolean;
		CropId: string;
		BrandId: string; 
		SeriesId: string; 
		HybridId: string; 
		TreatmentId: string;
		TreatmentKey: string;
	}>;

/** Convert the seed hierarchy to flat dictionaries. */
export function seedDataToLookups(seeds: CropAndBrandAvailability7[])
{
	const brands: BrandLookup = {};
	const seriesLookup: SeriesLookup = {};
	const hybrids: HybridLookup = {};
	const treatments: TreatmentLookup = {};
	const materials: MaterialLookup = {};
	const badAvailability:AvailabilityIndicator[] = ['None', 'Unknown'];
	for (const { Series, ...seed } of seeds)
	{
		brands[seed.BrandId] = seed;

		for (const { Hybrids, ...series } of Object.values(Series))
		{
			const IsAvailable = !badAvailability.some(bad => series.Availability === bad);
			seriesLookup[series.SeriesId] = {
				// This will be updated in a separate operation.
				HasAvailableTreatment: false,
				BrandId: seed.BrandId,
				CropId: seed.CropId,
				IsAvailable,
				...series,
			};

			for (const { Treatments, ...hybrid } of Object.values(Hybrids))
			{
				const IsAvailable = !badAvailability.some(bad => hybrid.Availability === bad);

				hybrids[hybrid.HybridId] = {
					BrandId: seed.BrandId,
					CropId: seed.CropId,
					SeriesId: series.SeriesId,
					IsAvailable,
					...hybrid,
				};

				for (const { MaterialAvailability, ...treatment } of Object.values(Treatments))
				{
					const IsAvailable = !badAvailability.some(bad => treatment.Availability === bad);

					// It's common to need to know if a series has a usable treatment at all.
					seriesLookup[series.SeriesId].HasAvailableTreatment = seriesLookup[series.SeriesId].HasAvailableTreatment || IsAvailable;

					// Treatment codes are not globally unique, so we must derive an id.
					const TreatmentKey = treatmentKey({ TreatmentId: treatment.TreatmentId, HybridId: hybrid.HybridId });
					treatments[TreatmentKey] = {
						BrandId: seed.BrandId,
						CropId: seed.CropId,
						SeriesId: series.SeriesId,
						HybridId: hybrid.HybridId,
						TreatmentKey,
						IsAvailable,
						...treatment,
					};

					for (const material of MaterialAvailability)
					{
						const IsAvailable = !badAvailability.some(bad => material.Availability === bad);

						materials[material.MaterialId] = {
							BrandId: seed.BrandId,
							CropId: seed.CropId,
							SeriesId: series.SeriesId,
							HybridId: hybrid.HybridId,
							TreatmentId: treatment.TreatmentId,
							TreatmentKey,
							IsAvailable,
							...material,
						};
					}
				}
			}
		}
	}

	return {
		brands,
		series: seriesLookup,
		hybrids,
		treatments,
		materials,
	};
}

/** A unique identifier for treatments. Treatment ids from the API are only unique in a products list when combined with a hybrid. */
export function treatmentKey(upper: { TreatmentId: string; HybridId: string; }): string
export function treatmentKey(lower: { treatmentId: string; hybridId: string; }): string
export function treatmentKey(treatmentId: string, hybridId: string): string
export function treatmentKey(a: Object | string, b?: string): string
{
	if (typeof a === 'object')
	{
		if (a.hasOwnProperty('TreatmentId'))
		{
			const { TreatmentId, HybridId } = a as unknown as any;
			return `${HybridId}-${TreatmentId}`;
		}
		else if (a.hasOwnProperty('treatmentId'))
		{
			const { treatmentId, hybridId } = a as unknown as any;
			return `${hybridId}-${treatmentId}`;
		}
		else
		{
			throw new Error('Object does not fit any overload for this function');
		}
	}
	else 
	{
		const hybridId = b;
		const treatmentId = a;
		return `${hybridId}-${treatmentId}`;
	}
}

/** 
 * Logic to select a treatment+hybrid within a series by default when a user has not selected one specifically.
 * Mainly used when a user has not selected a hybrid yet, so we can select the best treatment+hybrid combination.
 */
export function autoSelectTreatmentWithHybrid(
	hybridLookup: HybridLookup,
	treatmentLookup: TreatmentLookup,
	materialavailability: MaterialLookup,
	brandLookup: BrandLookup,
	seriesId: string,
	hybridId?: string
)
{
	// We're looking for a known hybrid, so look up the hybrid's treatments and return the best one
	if (hybridId)
	{
		const hybrid = hydrateHybrid(hybridLookup[hybridId], treatmentLookup, materialavailability);

		const treatment = autoSelectTreatmentForHybrid(hybrid, hybridLookup, brandLookup);
		const treatmentKeyValue = treatmentKey({ TreatmentId: treatment.TreatmentId, HybridId: hybrid.HybridId });

		return treatmentLookup[treatmentKeyValue];
	}

	// We don't know the hybrid yet, so find the best treatment + hybrid combination in the series
	const treatments = Object.values(treatmentLookup)
		.filter(t => t.IsAvailable && t.SeriesId === seriesId && (hybridId ? t.HybridId === hybridId : true));

	if (treatments.length <= 0)
		throw new Error(`No treatments found for series ${seriesId} and hybrid ${hybridId}`);

	type treatmentType = typeof treatments[0];

	let bestTreatment: treatmentType; 
	// Use most expensive if pricing is available.
	if (treatments.every(t => !!t.PricePerUnit))
	{
		bestTreatment = treatments.reduce((lowest, cur) => 
		{
			return cur.PricePerUnit < lowest.PricePerUnit ? cur : lowest;
		},treatments[0]);
	}
	// Use availablility if not.
	else 
	{
		const sortByAvailability = (a: treatmentType, b: treatmentType) => 
		{
			// Negatives means a "wins", postive means b "wins". 

			// Sort by longest hybrid name if same avail.
			if (a.Availability === b.Availability) 
			{
				const ah = hybridLookup[a.HybridId];
				const bh = hybridLookup[b.HybridId];
				return bh.HybridName.length - ah.HybridName.length;
			}
			
			const ranking: Record<AvailabilityIndicator, number> = {
				['None']: 0,
				['Unknown']: 1,
				['Red']: 2,
				['Yellow']: 3,
				['Green']: 4,
			};
			return ranking[b.Availability] - ranking[a.Availability];
		};

		bestTreatment = treatments.sort(sortByAvailability)[0];
	}

	// If we have a default treatment on the brand, use that.
	const defaultTreatmentId = brandLookup[bestTreatment.BrandId].DefaultTreatmentId;
	if (defaultTreatmentId && treatments.find(t => t.TreatmentId === defaultTreatmentId))
	{
		return treatments.find(t => t.TreatmentId === defaultTreatmentId);
	}

	return bestTreatment;
}

/** 
 * Logic to select a treatment from a hybrid by default when a user has not selected one specifically. 
 */
export function autoSelectTreatmentForHybrid(hybrid: HybridAvailability7, hybridLookup: HybridLookup, brandLookup: BrandLookup): TreatmentAvailability7
{
	const treatments = Object.values(hybrid.Treatments)
		.filter(t => t.Availability !== 'None' && t.Availability !== 'Unknown');
	
	// Throw an error if no treatments are available.
	if (treatments.length <= 0) 
	{
		throw new UserFriendlyException(
			`No treatments found for hybrid ${hybrid.HybridId}`,
			`No treatments are available for hybrid ${hybrid.HybridName}. Please select a different hybrid or check back later.`
		);
	}

	// If we have a default treatment, use that.
	const defaultTreatmentId = brandLookup[hybridLookup[hybrid.HybridId].BrandId].DefaultTreatmentId;
	if (defaultTreatmentId && treatments.find(t => t.TreatmentId === defaultTreatmentId))
	{
		return treatments.find(t => t.TreatmentId === defaultTreatmentId);
	}

	// If we have a base treament and it is available, use that.
	if (hybrid.BaseTreatmentId && treatments.find(t => t.TreatmentId === hybrid.BaseTreatmentId))
	{
		return treatments.find(t => t.TreatmentId === hybrid.BaseTreatmentId);
	}

	type treatmentType = typeof treatments[0];

	let bestTreatment: treatmentType;

	// Use cheapest if pricing is available.
	if (treatments.every(t => !!t.PricePerUnit))
	{
		bestTreatment = treatments.reduce((lowest, cur) => 
		{
			return cur.PricePerUnit < lowest.PricePerUnit ? cur : lowest;
		},treatments[0]);
	}
	// Use availablility if not.
	else 
	{
		const sortByAvailability = (a: treatmentType, b: treatmentType) => 
		{
			// Negatives means a "wins", postive means b "wins".			
			const ranking: Record<AvailabilityIndicator, number> = {
				['None']: 0,
				['Unknown']: 1,
				['Red']: 2,
				['Yellow']: 3,
				['Green']: 4,
			};
			return ranking[b.Availability] - ranking[a.Availability];
		};

		bestTreatment = treatments.sort(sortByAvailability)[0];
	}

	return bestTreatment;
}

export function hydrateHybrid(
	hybridLookupItem: HybridLookup[string],
	treatments: TreatmentLookup,
	materials: MaterialLookup
): HybridAvailability7
{
	const treatmentsForHybrid = Object.values(treatments).filter(
		t => t.HybridId === hybridLookupItem.HybridId && t.SeriesId === hybridLookupItem.SeriesId
	);

	const treatmentMap: Record<string, TreatmentAvailability7> = {};
	treatmentsForHybrid.forEach(t =>
	{
		treatmentMap[t.TreatmentId] = {
			TreatmentId: t.TreatmentId,
			TreatmentName: t.TreatmentName,
			Availability: t.Availability,
			PricePerUnit: t.PricePerUnit,
			PricingUnit: t.PricingUnit,
			MaterialAvailability: Object.values(materials).filter(
				m => m.TreatmentId === t.TreatmentId && m.HybridId === t.HybridId
			)
		};
	});

	return {
		HybridId: hybridLookupItem.HybridId,
		HybridName: hybridLookupItem.HybridName,
		TraitId: hybridLookupItem.TraitId,
		TraitName: hybridLookupItem.TraitName,
		TraitFullName: hybridLookupItem.TraitFullName,
		Availability: hybridLookupItem.Availability,
		BaseTreatmentId: hybridLookupItem.BaseTreatmentId,
		Treatments: treatmentMap
	};
}
